import { flow, isNull, set } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { DatasetType } from '../../../datasets/store/types';
import { PortfolioAnalyticsActionTypes, PortfolioQuestion, PortfolioAnalyticsState, PortfolioAnalysisSurveySummary, PortfolioAnalysisPage } from './types';

export const initialPortfolioQuestion: PortfolioQuestion = {
    parentDatasetId: null,
    datasetType: DatasetType.FORM,
    fieldId: null,
    fieldLabel: '',
    fieldType: null,
    sectionId: null,
    question: '',
    fieldDatasetId: null,
    groupFieldId: undefined,
    fieldIndex: null,
    groupFieldIndex: undefined,
    filters: {
        documentNameIds: []
    }
};

export const initialSurveySummary: PortfolioAnalysisSurveySummary = {
    agreementType: '',
    documentNames: [],
    fieldLabel: '',
    questionAsked: ''
};

export const INITIAL_STATE: PortfolioAnalyticsState = {
    portfolioQuestion: initialPortfolioQuestion,
    availableFields: [],
    questionResponse: null,
    isAskingQuestion: false,
    isChangingQuestion: false,
    downloadFilename: null,
    graphKeyColours: {},
    pieChartBase64: '',
    isDownloading: false,
    reportEmail: null,
    surveySummary: initialSurveySummary,
    pageView: PortfolioAnalysisPage.SELECT,
    templates: [],
    surveyIndex: 0,
    templateUsed: null,
    isFetchingTemplates: false
};

export const portfolioAnalyticsReducer: Reducer<PortfolioAnalyticsState> = (state = INITIAL_STATE, { type, payload }): PortfolioAnalyticsState => {
    switch (type) {
        case PortfolioAnalyticsActionTypes.UPDATE_PORTFOLIO_QUESTION: {
            const { key, value } = payload;
            return set(`portfolioQuestion[${key}]`, value, state);
        }
        case PortfolioAnalyticsActionTypes.SET_PORTFOLIO_FIELD: {
            let updatedPortfolioQuestion = state.portfolioQuestion;
            if (isNull(payload)) {
                updatedPortfolioQuestion = { ...state.portfolioQuestion, fieldId: null, fieldLabel: '', fieldType: null, sectionId: null, fieldDatasetId: null, groupFieldId: undefined, fieldIndex: null, groupFieldIndex: undefined, datasetType: DatasetType.FORM };
            } else {
                const { id, type, sectionId, datasetId, groupFieldId, fieldIndex, groupFieldIndex, datasetType, label } = payload;
                updatedPortfolioQuestion = { ...state.portfolioQuestion, fieldId: id, fieldLabel: label, fieldType: type, sectionId, fieldDatasetId: datasetId, groupFieldId, fieldIndex, groupFieldIndex, datasetType };
            }
            return set('portfolioQuestion', updatedPortfolioQuestion, state);
        }
        case PortfolioAnalyticsActionTypes.FETCH_DOCUMENT_FIELDS_SUCCESSFUL:
            return set('availableFields', payload, state);
        case PortfolioAnalyticsActionTypes.ASK_PORTFOLIO_QUESTION_STARTED:
            return set('isAskingQuestion', true, state);
        case PortfolioAnalyticsActionTypes.ASK_PORTFOLIO_QUESTION_SUCCESSFUL:
            return flow(
                set('questionResponse', payload),
                set('isAskingQuestion', false),
                set('pageView', PortfolioAnalysisPage.RESULTS)
            )(state);
        case PortfolioAnalyticsActionTypes.ASK_PORTFOLIO_QUESTION_FAILED:
            return set('isAskingQuestion', false, state);
        case PortfolioAnalyticsActionTypes.RESET_PORTFOLIO_SURVEY:
            return flow(
                set('portfolioQuestion', initialPortfolioQuestion),
                set('questionResponse', null),
                set('surveyIndex', 0),
                set('templateUsed', null)
            )(state);
        case PortfolioAnalyticsActionTypes.CLEAR_PORTFOLIO_RESULTS:
            return set('questionResponse', null, state);
        case PortfolioAnalyticsActionTypes.SET_IS_CHANGING_PORTFOLIO_SURVEY_QUESTION:
            return set('isChangingQuestion', payload, state);
        case PortfolioAnalyticsActionTypes.SET_FILE_NAME:
            return set('downloadFilename', payload, state);
        case PortfolioAnalyticsActionTypes.SET_KEY_COLOUR_PAIR:
            return set('graphKeyColours', payload, state);
        case PortfolioAnalyticsActionTypes.SET_PIE_CHART_BASE_64:
            return set('pieChartBase64', payload, state);
        case PortfolioAnalyticsActionTypes.DOWNLOAD_PORTFOLIO_ANALYTICS_STARTED:
            return set('isDownloading', true, state);
        case PortfolioAnalyticsActionTypes.DOWNLOAD_PORTFOLIO_ANALYTICS_SUCCESSFUL:
            return flow(
                set('downloadFilename', null),
                set('pieChartBase64', ''),
                set('isDownloading', false)
            )(state);
        case PortfolioAnalyticsActionTypes.DOWNLOAD_PORTFOLIO_ANALYTICS_FAILED:
            return set('isDownloading', false, state);
        case PortfolioAnalyticsActionTypes.SET_SURVEY_SUMMARY:
            return set('surveySummary', payload, state);
        case PortfolioAnalyticsActionTypes.SET_PORTFOLIO_ANALYSIS_PAGE_VIEW:
            return set('pageView', payload, state);
        case PortfolioAnalyticsActionTypes.SET_SURVEY_INDEX:
            return set('surveyIndex', payload, state);
        case PortfolioAnalyticsActionTypes.GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_STARTED:
            return set('isFetchingTemplates', true, state);
        case PortfolioAnalyticsActionTypes.GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_SUCCESSFUL:
            return flow(
                set('templates', payload),
                set('isFetchingTemplates', false)
            )(state);
        case PortfolioAnalyticsActionTypes.GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_FAILED:
            return set('isFetchingTemplates', false, state);
        case PortfolioAnalyticsActionTypes.DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED:
            return set('isFetchingTemplates', true, state);
        case PortfolioAnalyticsActionTypes.DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_SUCCESSFUL:
            return flow(
                set('templates', payload),
                set('isFetchingTemplates', false)
            )(state);
        case PortfolioAnalyticsActionTypes.DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_FAILED:
            return set('isFetchingTemplates', false, state);
        case PortfolioAnalyticsActionTypes.SET_PORTFOLIO_QUESTION:
            return set('portfolioQuestion', payload, state);
        case PortfolioAnalyticsActionTypes.SET_PORTFOLIO_ANALYTICS_TEMPLATE:
            return set('templateUsed', payload, state);
        case PortfolioAnalyticsActionTypes.SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED:
            return set('isFetchingTemplates', true, state);
        case PortfolioAnalyticsActionTypes.SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_SUCCESSFUL:
            return flow(
                set('templates', payload),
                set('isFetchingTemplates', false)
            )(state);
        case PortfolioAnalyticsActionTypes.SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_FAILED:
            return set('isFetchingTemplates', false, state);
        case PortfolioAnalyticsActionTypes.UPDATE_PORTFOLIO_ANALYTICS_DATE_FILTER: {
            const { key, value } = payload;
            return set(`portfolioQuestion.filters.date[${key}]`, value, state);
        }
        case PortfolioAnalyticsActionTypes.UPDATE_PORTFOLIO_ANALYTICS_FILTER: {
            const { key, value } = payload;
            return set(`portfolioQuestion.filters[${key}]`, value, state);
        }
        case PortfolioAnalyticsActionTypes.TOGGLE_EMAIL_MODAL: {
            const filename = state.templateUsed?.templateName || '';
            const reportEmail = isNull(payload) ? null : flow(
                set('filename', filename),
                set('userIds', []),
                set('emailBody', null)
            )(payload);
            return set('reportEmail', reportEmail, state);
        }
        case PortfolioAnalyticsActionTypes.UPDATE_PORTFOLIO_ANALYTICS_EMAIL_VALUE: {
            const { key, value } = payload;
            return set(`reportEmail[${key}]`, value, state);
        }
        case PortfolioAnalyticsActionTypes.EMAIL_PORTFOLIO_ANALYTICS_SUCCESSFUL:
            return set('reportEmail', null, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
